import { ModeCategory, ModeName } from '@commutifi/modes'
import omit from 'lodash/omit'
import React from 'react'
import { AddressAutoCompleteFormInput } from '@commutifi-fe/commutifi-specific/Presentation/AddressAutocomplete'
import { Button as AntButton, Form, Modal, ModalFuncProps, Question, Radio, CardGridSize } from '@commutifi-fe/ui'
import { Themes } from '@commutifi-fe/ui/components/theme/constants'
import ModeCardsSelector from 'shared/components/common/CommuteModePicker/components/ModeCardsSelector'
import GoogleTransitDirections from 'shared/components/common/GoogleTransitDirections'
import { AddressPoint, CommuteTransport, TransitOption } from 'shared/interfaces/Commute'
import { FormattedMessage, useIntl } from 'locales/index'
import { getArrayValue } from 'utils/index'
import { logger } from 'utils/logRocket'
import { validateAddress } from 'utils/helpers/address'

export interface Step {
  transport: CommuteTransport
  until: string
  addressPoint: AddressPoint
  // Transit is basically a sub route generated from google (or maybe another service)
  // to autocomplete the user route and simplify the generation of his commute route
  transit?: TransitOption
}

interface InterStepModalProps extends ModalFuncProps {
  onSave?: (step: Step[]) => void
  value?: Step
  // For location biasing in the step Modal address selection
  startPoint: AddressPoint
  endPoint: AddressPoint
  arrivalTime?: string
  timezone?: string | null
  // Initial value of 'until' field
  initialUntil?: string
  // I know this is not the best option but we need a way to predefine fields
  // based on the fact that we know the user is driving to some point and
  // transiting from that point until he gets to work
  isDriveAndTransit?: boolean
  theme?: Themes | `${Themes}`
}

type SpaceTimePoint = AddressPoint & { time?: string; timezone?: string | null }

export const at = 'I_am_at'
export const atWork = 'I_am_at_work'
const FIELD_NAMES = {
  transport: 'transport',
  until: 'until',
  address: 'addressPoint',
  transit: 'transit'
}

function InterStepModal({
  title,
  open,
  startPoint,
  endPoint,
  arrivalTime,
  timezone,
  onCancel,
  onSave,
  value,
  initialUntil,
  isDriveAndTransit,
  theme
}: InterStepModalProps) {
  const intl = useIntl()
  const [form] = Form.useForm()
  const { validateFields } = form
  const untilInitialValue = initialUntil || (isDriveAndTransit ? at : value?.until || at)
  const addressInitialValue = value?.addressPoint
  const transportInitialValue = isDriveAndTransit
    ? { category: ModeCategory.Drive, mode: ModeName.Drive }
    : value?.transport

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    if (onCancel) {
      onCancel(e)
    }
  }

  const handleSave = (values: Step | Step[]) => {
    if (onSave) {
      onSave(getArrayValue(values))
    }
  }
  const handleSubmit = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    try {
      const values = (await validateFields()) as Step
      // Handle drive and transit case
      if (values.transport.category === ModeCategory.Drive && values.transit && isDriveAndTransit) {
        handleSave([
          // Split into 2 step: Drive and transit to work
          omit(values, 'transit'),
          {
            transit: values.transit,
            until: atWork,
            transport: { category: ModeCategory.PublicTransit },
            addressPoint: { location: values.transit.endLocation, formattedAddress: values.transit.endAddress || '' }
          }
        ])
      } else {
        handleSave(values)
      }
      form.resetFields()
    } catch (info) {
      logger.log('Validation Failed: ', info)
    }
  }

  const renderUntilField = () => (
    <Radio.Group buttonStyle="solid" block>
      <Radio.Button value={at}>
        <FormattedMessage id="commuteRoute.steps.endPoint.labels.imAt" />
      </Radio.Button>
      <Radio.Button value={atWork}>
        <FormattedMessage id="commuteRoute.steps.endPoint.labels.imAtWork" />
      </Radio.Button>
    </Radio.Group>
  )

  const renderAddressAutocompleteField = () => (
    <AddressAutoCompleteFormInput>
      <AddressAutoCompleteFormInput.Map
        startLocation={startPoint.location}
        endLocation={endPoint.location}
        theme={theme}
      />
    </AddressAutoCompleteFormInput>
  )

  const renderTransitSelector = (from: SpaceTimePoint, to: SpaceTimePoint) => (
    <GoogleTransitDirections
      startLocation={from.location}
      startAddress={from.formattedAddress}
      endLocation={to.location}
      endAddress={to.formattedAddress}
      departureTime={from.time}
      arrivalTime={to.time}
      timezone={to.timezone || from.time}
    >
      <GoogleTransitDirections.OtherOption />
    </GoogleTransitDirections>
  )

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      title={title}
      footer={
        <AntButton
          type="primary"
          block
          onClick={handleSubmit}
          className="u-margin-top--small u-margin-bottom--large u-margin-horizontal--medium"
        >
          <span>
            <FormattedMessage id="commuteRoute.steps.button.save" />
          </span>
        </AntButton>
      }
      data-testid="commute-timeline-modal"
    >
      <Form
        form={form}
        initialValues={{
          [FIELD_NAMES.transport]: transportInitialValue,
          [FIELD_NAMES.until]: untilInitialValue,
          [FIELD_NAMES.address]: addressInitialValue
        }}
        layout="vertical"
        name="commute_timeline_step_modal"
        requiredMark={false}
      >
        <Form.Item shouldUpdate noStyle>
          {({ setFieldsValue, getFieldValue }) => (
            // When the user select transit as the mode we propose transit
            // routes until his work unless an address is already chosen

            <Form.Item
              name={FIELD_NAMES.transport}
              initialValue={transportInitialValue}
              rules={[{ required: true }]}
              className="u-padding-bottom--large-2"
              label={<Question title={<FormattedMessage id="commuteRoute.steps.firstMode.question" />} />}
            >
              <ModeCardsSelector
                onChange={(transport) => {
                  if (transport?.category === ModeCategory.PublicTransit && !getFieldValue(FIELD_NAMES.address)) {
                    setFieldsValue({ [FIELD_NAMES.until]: atWork })
                  }
                }}
                cardSize={CardGridSize.Small}
                equalWidth
                className="u-padding-top--small"
              />
            </Form.Item>
          )}
        </Form.Item>
        <Question title={<FormattedMessage id="commuteRoute.steps.endPoint.question" />}>
          <Form.Item name={FIELD_NAMES.until} rules={[{ required: true }]}>
            {renderUntilField()}
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) =>
              getFieldValue(FIELD_NAMES.until) !== atWork && (
                <Form.Item
                  name={FIELD_NAMES.address}
                  rules={[
                    {
                      validator: (rule: any, options: any) =>
                        validateAddress(rule, options, { officeLocation: endPoint.location, intl })
                    }
                  ]}
                >
                  {renderAddressAutocompleteField()}
                </Form.Item>
              )
            }
          </Form.Item>
        </Question>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            // Case 1: Drive + Transit
            //         Drive is pre-selected and 'until' value should be to a location not work.
            //         So if the user changes until to be at work then it means we drive to work
            //         and we don't show the transit options
            // Case 2: Transit
            //         We know the user transit so as soon as we have the address or we know he
            //         transits all the way to work we can display the transit options
            const address: AddressPoint | null = getFieldValue(FIELD_NAMES.address)
            const until: string = getFieldValue(FIELD_NAMES.until) || untilInitialValue
            const transportCategory: string = getFieldValue(FIELD_NAMES.transport)?.category
            const driveAndTransit = isDriveAndTransit && address && until !== atWork
            const transitOnlyUntilLocation = address && transportCategory === ModeCategory.PublicTransit
            const transitOnlyUntilWork = until === atWork && transportCategory === ModeCategory.PublicTransit
            const showTransitOptions = driveAndTransit || transitOnlyUntilLocation || transitOnlyUntilWork
            return (
              showTransitOptions && (
                <Form.Item
                  name={FIELD_NAMES.transit}
                  rules={[{ required: true, message: <FormattedMessage id="required" /> }]}
                  label={
                    <Question
                      title={
                        driveAndTransit ? (
                          <FormattedMessage id="commuteRoute.timeline.driveTransit.question" />
                        ) : (
                          <FormattedMessage id="commuteRoute.timeline.transit.question" />
                        )
                      }
                    />
                  }
                >
                  {renderTransitSelector(
                    // Drive + Transit means we drive until a location and THAT location
                    // is the start of the transit. Otherwise it is from the start of the route
                    driveAndTransit ? { ...address, timezone } : { ...startPoint, timezone },
                    // The only case were the end of the transit is not the work place is when
                    // the user select transit and he chose an address. But, in all case we send the
                    // arrival time as the time the user arrives at the office because we have no home
                    // departure time and we can't know the inter step arrival time as we don't know
                    // the end of the route when the transit path is not the last part
                    transitOnlyUntilLocation
                      ? { ...address, time: arrivalTime, timezone }
                      : { ...endPoint, time: arrivalTime, timezone }
                    // This is not the perfect logic but we are trying to infer the timezone and time we want
                    // to use to call google when we don't know the exact times except for the departure and arrival times
                    // driveAndTransit || transitOnlyUntilWork ? endPoint.timezone : startPoint.timezone
                  )}
                </Form.Item>
              )
            )
          }}
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default InterStepModal
